import {
  Alert,
  Badge,
  Card,
  Center,
  Group,
  Loader,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { IconCheck, IconX } from '@tabler/icons-react';
import { useMemo } from 'react';
import { UnifyTable, type UnifyTableColumn } from '@/components/UnifyTable';
import { LocalhostOnlyBadge } from '@/components/LocalhostOnlyBadge';
import { FEATURE_VISIBILITY } from '@/config/featureVisibility';
import type {
  ResourcePermissionRow,
  RolePermissionsViewModel,
  UiPermissionRow,
} from '@/features/rbac/rolePermissionsView';

export interface RolePermissionsViewProps {
  viewModel: RolePermissionsViewModel;
  loading?: boolean;
  error?: string | null;
}

function PermissionFlag({ enabled }: { enabled: boolean }): JSX.Element {
  return enabled ? (
    <IconCheck size={16} color="var(--mantine-color-green-7)" aria-label="Yes" />
  ) : (
    <IconX size={16} color="var(--mantine-color-gray-5)" aria-label="No" />
  );
}

const resourceColumns: UnifyTableColumn<ResourcePermissionRow>[] = [
  { key: 'resourceType', label: 'Resource type', sortable: true },
  {
    key: 'read',
    label: 'Read',
    align: 'center',
    width: 72,
    render: (row) => <PermissionFlag enabled={row.read} />,
  },
  {
    key: 'search',
    label: 'Search',
    align: 'center',
    width: 72,
    render: (row) => <PermissionFlag enabled={row.search} />,
  },
  {
    key: 'create',
    label: 'Create',
    align: 'center',
    width: 72,
    render: (row) => <PermissionFlag enabled={row.create} />,
  },
  {
    key: 'update',
    label: 'Update',
    align: 'center',
    width: 72,
    render: (row) => <PermissionFlag enabled={row.update} />,
  },
  {
    key: 'delete',
    label: 'Delete',
    align: 'center',
    width: 72,
    render: (row) => <PermissionFlag enabled={row.delete} />,
  },
  {
    key: 'scope',
    label: 'Scope / criteria',
    render: (row) => (
      <Stack gap={2}>
        {row.scope ? (
          <Text size="sm" c="dimmed">
            {row.scope}
          </Text>
        ) : null}
        {row.conditionSummary ? (
          <Text size="sm">{row.conditionSummary}</Text>
        ) : (
          !row.scope && (
            <Text size="sm" c="dimmed">
              —
            </Text>
          )
        )}
        {row.warning ? (
          <Text size="xs" c="orange.7">
            {row.warning}
          </Text>
        ) : null}
      </Stack>
    ),
  },
];

export function RolePermissionsView({
  viewModel,
  loading = false,
  error = null,
}: RolePermissionsViewProps): JSX.Element {
  const resourceTableKey = useMemo(
    () => `${viewModel.role}-resource-${viewModel.resourceRows.length}`,
    [viewModel.role, viewModel.resourceRows.length],
  );
  const uiRowsByCategory = useMemo(() => {
    const grouped = new Map<string, UiPermissionRow[]>();
    for (const row of viewModel.uiRows) {
      const existing = grouped.get(row.category) ?? [];
      existing.push(row);
      grouped.set(row.category, existing);
    }
    return Array.from(grouped.entries())
      .map(([category, rows]) => ({
        category,
        rows: rows.sort((a, b) => a.permission.localeCompare(b.permission)),
      }))
      .sort((a, b) => a.category.localeCompare(b.category));
  }, [viewModel.uiRows]);

  if (loading) {
    return (
      <Center py="xl">
        <Loader size="md" />
      </Center>
    );
  }

  return (
    <Stack gap="lg">
      {error ? (
        <Alert color="red" title="Could not load permissions">
          {error}
        </Alert>
      ) : null}

      {viewModel.warnings.length > 0 ? (
        <Alert color="yellow" title="Notes">
          <Stack gap={4}>
            {viewModel.warnings.map((warning) => (
              <Text key={warning} size="sm">
                {warning}
              </Text>
            ))}
          </Stack>
        </Alert>
      ) : null}

      {FEATURE_VISIBILITY.profileRolePermissions ? (
      <Stack gap="md">
        <div>
          <Group gap="sm" align="center" wrap="wrap">
            <Title order={5}>Resource permissions (AccessPolicy)</Title>
            <LocalhostOnlyBadge />
          </Group>
          <Text size="sm" c="dimmed" mt={4}>
            Server-side FHIR access enforced by Medplum AccessPolicy.
          </Text>
        </div>
        {viewModel.resourceRows.length === 0 ? (
          <Text size="sm" c="dimmed">
            No resource permission rows to display.
          </Text>
        ) : (
          <UnifyTable
            key={resourceTableKey}
            data={viewModel.resourceRows}
            columns={resourceColumns}
            getRowKey={(row) => row.rowKey}
          />
        )}
      </Stack>
      ) : null}

      {FEATURE_VISIBILITY.profileRolePermissions ? (
      <Card withBorder p="lg">
        <Stack gap="md">
          <div>
            <Group gap="sm" align="center" wrap="wrap">
              <Title order={5}>UI permissions</Title>
              <LocalhostOnlyBadge />
            </Group>
            <Text size="sm" c="dimmed" mt={4}>
              App navigation and feature gates from the ui-permissions extension.
            </Text>
          </div>
          {viewModel.uiRows.length === 0 ? (
            <Text size="sm" c="dimmed">
              No UI permissions to display. The ui-permissions extension may be missing on the
              source resource for this role.
            </Text>
          ) : (
            <Stack gap="lg">
              {uiRowsByCategory.map((group) => (
                <Stack key={group.category} gap="xs">
                  <Title order={6}>{group.category}</Title>
                  {group.rows.map((row) => (
                    <Group key={row.permissionId} justify="space-between" wrap="nowrap">
                      <Text size="sm">{row.permission}</Text>
                      <Badge color={row.enabled ? 'green' : 'gray'} variant="light" size="sm">
                        {row.enabled ? 'Enabled' : 'Disabled'}
                      </Badge>
                    </Group>
                  ))}
                </Stack>
              ))}
            </Stack>
          )}
        </Stack>
      </Card>
      ) : null}
    </Stack>
  );
}
